
import { GoogleGenAI, Type } from "@google/genai";
import { Website } from "../types";

export interface GenerationConfig {
  name: string;
  htmlFiles: Array<{ name: string; content: string }>;
  primaryColor: string;
  secondaryColor: string;
}

/**
 * Uses AI to clean up and unify extracted CSS from multiple files
 */
export const optimizeStylesWithAI = async (rawCss: string, projectName: string): Promise<string> => {
  if (!rawCss.trim()) return "";
  
  // Create a new instance right before making an API call to ensure it uses the most up-to-date API key
  const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });

  const prompt = `
    You are a CSS expert. I have extracted these styles from a website project called "${projectName}".
    Clean up this CSS:
    1. Remove duplicate selectors.
    2. Format it cleanly.
    3. Ensure no conflicts with standard browser resets.
    4. Keep all original class names and variables.
    
    CSS TO CLEAN:
    ${rawCss}
    
    Return ONLY the cleaned CSS code. No markdown, no explanations.
  `;

  try {
    const response = await ai.models.generateContent({
      model: "gemini-3-flash-preview",
      contents: prompt
    });
    return response.text || rawCss;
  } catch (e) {
    console.warn("AI Style optimization failed, using raw CSS.");
    return rawCss;
  }
};

/**
 * Uses AI to modify the website configuration based on user natural language requests
 */
export const editWebsiteWithAI = async (website: Website, prompt: string): Promise<Website> => {
  // Create a new instance right before making an API call to ensure it uses the most up-to-date API key
  const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });

  const response = await ai.models.generateContent({
    model: "gemini-3-pro-preview",
    contents: `
      You are a world-class web designer and frontend engineer.
      Modify the following website configuration based on this request: "${prompt}"
      
      CRITICAL INSTRUCTIONS:
      1. If the user asks for layout changes, update the 'sections' array.
      2. If the user asks for style changes, update 'primaryColor', 'secondaryColor', or 'customCss'.
      3. Maintain the existing 'id' fields for sections unless creating new ones.
      4. Ensure all 'html' strings in sections are valid and styled inline or via 'customCss'.
      
      CURRENT WEBSITE CONFIGURATION:
      ${JSON.stringify(website, null, 2)}
      
      Return the ENTIRE updated website object in JSON format.
    `,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          id: { type: Type.STRING },
          tenantId: { type: Type.STRING },
          name: { type: Type.STRING },
          domain: { type: Type.STRING },
          primaryColor: { type: Type.STRING },
          secondaryColor: { type: Type.STRING },
          customCss: { type: Type.STRING },
          pages: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                name: { type: Type.STRING },
                path: { type: Type.STRING },
                sections: {
                  type: Type.ARRAY,
                  items: {
                    type: Type.OBJECT,
                    properties: {
                      id: { type: Type.STRING },
                      type: { type: Type.STRING },
                      content: {
                        type: Type.OBJECT,
                        properties: {
                          html: { type: Type.STRING },
                          title: { type: Type.STRING },
                          subtitle: { type: Type.STRING },
                          buttonText: { type: Type.STRING },
                          imageUrl: { type: Type.STRING },
                        }
                      }
                    },
                    required: ["id", "type", "content"]
                  }
                }
              },
              required: ["name", "path", "sections"]
            }
          },
          marketing: {
            type: Type.OBJECT,
            properties: {
              promoBars: { 
                type: Type.ARRAY, 
                items: { 
                  type: Type.OBJECT,
                  properties: {
                    id: { type: Type.STRING },
                    isActive: { type: Type.BOOLEAN },
                    text: { type: Type.STRING },
                    buttonText: { type: Type.STRING },
                    link: { type: Type.STRING },
                    color: { type: Type.STRING }
                  }
                } 
              },
              popups: { 
                type: Type.ARRAY, 
                items: { 
                  type: Type.OBJECT,
                  properties: {
                    id: { type: Type.STRING },
                    isActive: { type: Type.BOOLEAN },
                    title: { type: Type.STRING },
                    description: { type: Type.STRING },
                    imageUrl: { type: Type.STRING },
                    activeCampaign: { type: Type.STRING }
                  }
                } 
              },
              games: { 
                type: Type.ARRAY, 
                items: { 
                  type: Type.OBJECT,
                  properties: {
                    id: { type: Type.STRING },
                    type: { type: Type.STRING },
                    isActive: { type: Type.BOOLEAN },
                    title: { type: Type.STRING }
                  }
                } 
              }
            }
          }
        },
        required: ["id", "tenantId", "name", "domain", "primaryColor", "secondaryColor", "pages", "marketing"]
      }
    }
  });

  try {
    const text = response.text;
    if (!text) throw new Error("Empty AI response");
    const updatedWebsite = JSON.parse(text) as Website;
    return updatedWebsite;
  } catch (error) {
    console.error("Failed to parse AI website update:", error);
    return website;
  }
};
